import React, { Component, PropTypes } from 'react';
import { dateFormats, keyCodes } from '../../util/constants';
import moment from 'moment';
import { MonthView } from 'react-date-picker';
import FormControl from './FormControl';
import { Icon, OnClickOut, Popover } from 'react-fast-ui';
import DelayedOnClickOut from '../DelayedOnClickOut';
import strtotime from 'strtotime';
import { isBlank } from 'underscore.string';

const { isContained } = OnClickOut;
const CalendarIcon = props => (
  <span className="cursor-pointer" {...props} >
    <Icon key="icon" name="calendar"/>
  </span>
);

const CALENDAR_ICON_POSITION = {
  position: 'absolute', right: 0, top: 0, bottom: 0
};

function getDisplayValue({ value, displayFormat }) {
  if (typeof value === 'string' && value.length > 0) {
    const dateMoment = moment(value, dateFormats.ISO_8601, true);

    if (dateMoment && dateMoment.isValid()) {
      return dateMoment.format(displayFormat);
    }
  }

  return '';
}

export default class DatePicker extends Component {
  static propTypes = {
    displayFormat: PropTypes.string,
    blurOnChange: PropTypes.bool,
    clearable: PropTypes.bool,
    calendarProps: PropTypes.object,
    inputProps: PropTypes.object
  };

  static defaultProps = {
    displayFormat: 'l',
    blurOnChange: true,
    calendarProps: null,
    inputProps: null
  };

  state = {
    open: false,
    typing: getDisplayValue(this.props)
  };

  componentWillReceiveProps({ value, displayFormat }) {
    if (value !== this.props.value) {
      this.changeTyping(getDisplayValue({ value, displayFormat }));
    }
  }

  clearValue = () => this.handleChange(null);

  renderClearIcon() {
    const { clearable, value } = this.props;

    if (clearable && value) {
      return (
        <strong className="mg-right-sm cursor-pointer color-danger" style={{ fontSize: 24 }} onClick={this.clearValue}>
          ×
        </strong>
      );
    } else {
      return null;
    }
  }

  focus = () => {
    const { input } = this.refs;
    if (input) {
      input.focus();
    }
  };

  open = () => this.setState({ open: true });
  close = () => this.setState({ open: false });

  handleClickOut = e => {
    const { open } = this.state;

    if (open) {
      const { inputDiv } = this.refs;
      if (inputDiv) {
        if (!isContained(e.target, inputDiv)) {
          this.close();
        }
      }
    }
  };

  handleChange = date => {
    const { clearable, blurOnChange, onChange } = this.props;

    if (!date && !clearable) {
      return;
    }

    if (blurOnChange) {
      this.close();
      this.refs.next.focus();
    }

    onChange(date);
  };

  renderCalendar() {
    const { value, calendarProps } = this.props;

    return (
      <DelayedOnClickOut onClickOut={this.handleClickOut}>
        <MonthView
          key={value}
          weekNumbers={false} highlightWeekends={false} {...calendarProps} date={value}
          onChange={this.handleChange}
        />
      </DelayedOnClickOut>
    );
  }

  handleFocus = e => {
    this.open();

    const { onFocus } = this.props;
    if (onFocus) {
      onFocus(e);
    }
  };

  handleKeyDown = e => {
    const { open } = this.state;
    const { onKeyDown } = this.props;

    switch (e.keyCode) {
      case keyCodes.KEY_TAB:
        if (open) {
          this.close();
        }
        break;

      case keyCodes.KEY_ESCAPE:
        if (open) {
          this.close();
          e.preventDefault();
          e.stopPropagation();
        }
        break;

      case keyCodes.KEY_ENTER:
        this.triggerTypingSave();
        break;

      case keyCodes.KEY_BACKSPACE:
      case keyCodes.KEY_DELETE:
        if (e.target.value.length === 0) {
          this.triggerTypingSave();
        }
        break;

      default:
        if (onKeyDown) {
          onKeyDown(e);
        }
        break;
    }
  };

  changeTyping = typing => this.setState({ typing });
  handleTypingChange = e => this.changeTyping(e.target.value);
  handleBlur = e => {
    this.triggerTypingSave();
  };

  triggerTypingSave = () => {
    const { displayFormat, value, onChange } = this.props;
    const { typing } = this.state;

    if (getDisplayValue({ value, displayFormat }) !== typing) {
      const typingEpochSeconds = !isBlank(typing) ? strtotime(typing) : null;

      if (typingEpochSeconds && typingEpochSeconds > 0) {
        const isoValue = moment.utc(typingEpochSeconds * 1000).format(dateFormats.ISO_8601);

        this.setState({
          typing: getDisplayValue({ value: isoValue, displayFormat })
        }, () => onChange(isoValue));
      } else {
        this.setState({ typing: '' }, () => onChange(null));
      }
    }
  };

  render() {
    const { className, style, disabled } = this.props;
    const { open, typing } = this.state;

    return (
      <div style={{ ...style, position: 'relative' }} ref="inputDiv" className={className}>
        <FormControl>
          <input
            placeholder="Click to select..."
            ref="input"
            disabled={disabled}
            type="text"
            onKeyDown={this.handleKeyDown}
            onFocus={this.handleFocus}
            onBlur={this.handleBlur}
            onChange={this.handleTypingChange}
            value={typing}
          />
        </FormControl>
        <div ref="next" tabIndex={-1} style={{ position: 'absolute' }}/>

        <div style={CALENDAR_ICON_POSITION}>
          <Popover content={open ? this.renderCalendar() : <span/>} open={open} defaultPlacement="bottom">
            <div className="display-inline-block pd-sm" style={{ height: '100%' }}>
              <CalendarIcon onClick={this.focus}/>
            </div>
          </Popover>
        </div>

        <div style={{ position: 'absolute', right: 22, top: '50%', transform: 'translateY(-50%)' }}>
          {this.renderClearIcon()}
        </div>
      </div>
    );
  }
}



// WEBPACK FOOTER //
// ./src/js/components/inputs/DatePicker.jsx